import { useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/lib/supabase'
import { completeOnboarding } from '@/lib/userProfile'

interface RequireOnboardingProps {
  children: React.ReactNode
}

export function RequireOnboarding({ children }: RequireOnboardingProps) {
  const { user, profile, loading, refreshProfile } = useAuth()
  const location = useLocation()
  const [resolved, setResolved] = useState(false)
  const [isOnboarded, setIsOnboarded] = useState(false)

  useEffect(() => {
    let isCancelled = false

    const resolveOnboarding = async () => {
      if (!user) {
        setResolved(true)
        setIsOnboarded(false)
        return
      }

      if (profile?.onboarding_completed_at) {
        setResolved(true)
        setIsOnboarded(true)
        return
      }

      try {
        const { data, error } = await supabase
          .from('areas')
          .select('id')
          .eq('user_id', user.id)
          .limit(1)

        if (error) {
          throw error
        }

        if ((data?.length ?? 0) > 0) {
          await completeOnboarding({
            writingType: profile?.onboarding_writing_type ?? null,
          })
          await refreshProfile()

          if (!isCancelled) {
            setIsOnboarded(true)
          }
        } else if (!isCancelled) {
          setIsOnboarded(false)
        }
      } catch (error) {
        console.error('Error resolving onboarding status:', error)
        if (!isCancelled) {
          setIsOnboarded(false)
        }
      } finally {
        if (!isCancelled) {
          setResolved(true)
        }
      }
    }

    void resolveOnboarding()

    return () => {
      isCancelled = true
    }
  }, [profile?.onboarding_completed_at, profile?.onboarding_writing_type, refreshProfile, user])

  if (loading || !resolved) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  if (!isOnboarded) {
    return <Navigate to="/onboarding" replace state={{ from: location }} />
  }

  return <>{children}</>
}
